import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import { activationColor } from './bonusStatus';

const X_CAP = 3;
const Y_CAP = 3;
const X_SPLIT = 1;
const Y_SPLIT = 0.5;

// left = YAxis width (64) + chart margin.left (0)
// bottom = chart margin.bottom (4) + XAxis height (~26)
const PLOT = { top: 8, right: 16, bottom: 30, left: 64 };

const BUCKETS = [
  { key: 'high', label: 'Activation > 60%', color: '#00c896' },
  { key: 'mid',  label: 'Activation 30–60%', color: '#e0a23c' },
  { key: 'low',  label: 'Activation < 30%', color: '#e05555' },
  { key: 'none', label: 'No bonuses', color: '#6b7280' },
];

const QUADRANTS = [
  { name: 'Retention risk',      corner: 'top-left',     color: '#e05555', desc: "Turnover sliding while heavily bonus-funded — the bonuses aren't holding them. Work these first." },
  { name: 'Declining · low bonus', corner: 'bottom-left', color: '#e0a23c', desc: 'Sliding but barely bonused — genuine disengagement, or a candidate for a targeted offer.' },
  { name: 'Bonus-driven growth', corner: 'top-right',    color: '#e0a23c', desc: 'Growing, but bonus-dependent — check whether the growth would survive without the bonuses.' },
  { name: 'Healthy',             corner: 'bottom-right', color: '#00c896', desc: 'Growing on their own money — low-touch, no need to push more bonuses at them.' },
];

const QUADRANT_ZOOM = {
  retention_risk: { x: [0, X_SPLIT],    y: [Y_SPLIT, Y_CAP] },
  declining_low:  { x: [0, X_SPLIT],    y: [0, Y_SPLIT] },
  bonus_growth:   { x: [X_SPLIT, X_CAP], y: [Y_SPLIT, Y_CAP] },
  healthy:        { x: [X_SPLIT, X_CAP], y: [0, Y_SPLIT] },
};

const QUADRANT_LABEL_SX = { fontSize: 13, fontWeight: 700, fill: '#8a93a8' };

function bucketOf(p) {
  if (!p.total_bonuses) return 'none';
  const r = p.activation_rate;
  if (r > 0.6) return 'high';
  if (r >= 0.3) return 'mid';
  return 'low';
}

function fmtEur(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return '€' + (n / 1000000).toFixed(2) + 'M';
  if (abs >= 1000) return '€' + (n / 1000).toFixed(1) + 'K';
  return '€' + Math.round(n).toLocaleString('en-GB');
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: '#0d0f12', border: '1px solid #2a3042', borderRadius: 4, padding: '8px', fontSize: 11 }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: '#d8dde8', margin: '0 0 2px' }}>
        {p.email || `User ${p.user_id}`}
      </p>
      <div style={{ color: '#5a6477' }}>ID {p.user_id} · {fmtEur(p.total_stake_eur)} total turnover</div>
      <div style={{ color: '#4fc3f7' }}>
        Recent÷prior: {p.recent_vs_prior_stake_ratio != null ? p.recent_vs_prior_stake_ratio.toFixed(2) + '×' : '—'}
      </div>
      <div style={{ color: '#e0a23c' }}>
        Bonus÷deposits: {p.bonus_to_deposit_ratio != null ? (p.bonus_to_deposit_ratio * 100).toFixed(1) + '%' : '—'}
        {p.total_deposits_eur != null && <span style={{ color: '#5a6477' }}> ({fmtEur(p.total_deposits_eur)} deposited)</span>}
      </div>
      <div style={{ color: activationColor(p.total_bonuses ? p.activation_rate : null) }}>
        Activation: {p.total_bonuses ? Math.round(p.activation_rate * 100) + '%' : 'no bonuses'}
      </div>
      <p style={{ fontSize: 9, color: '#5a6477', margin: '4px 0 0' }}>click to open this player</p>
    </div>
  );
}

function Dot({ cx, cy, fill, payload, onSelect, maxStake }) {
  if (cx == null || cy == null) return null;
  const t = maxStake > 0 ? (payload.total_stake_eur || 0) / maxStake : 0;
  const r = 4 + 14 * Math.sqrt(t);
  return (
    <circle
      cx={cx} cy={cy} r={r} fill={fill} fillOpacity={0.85} stroke="#0d0f12" strokeWidth={1}
      style={{ cursor: 'pointer' }}
      onClick={() => onSelect && payload && onSelect(payload.user_id)}
    />
  );
}

function pixelToData(px, py, rect, xDom, yDom) {
  const plotW = rect.width  - PLOT.left - PLOT.right;
  const plotH = rect.height - PLOT.top  - PLOT.bottom;
  const xFrac = Math.max(0, Math.min(1, (px - PLOT.left)  / plotW));
  const yFrac = Math.max(0, Math.min(1, 1 - (py - PLOT.top) / plotH));
  return {
    x: xDom[0] + xFrac * (xDom[1] - xDom[0]),
    y: yDom[0] + yFrac * (yDom[1] - yDom[0]),
  };
}

export default function CohortScatter({ players, onSelect, quadrantFilter }) {
  const containerRef = useRef(null);
  const [dragZoom, setDragZoom]   = useState(null); // { x: [min,max], y: [min,max] }
  const [selStart, setSelStart]   = useState(null);
  const [selEnd,   setSelEnd]     = useState(null);
  const [selecting, setSelecting] = useState(false);

  // clear drag zoom when quadrant filter changes
  useEffect(() => { setDragZoom(null); }, [quadrantFilter]);

  const maxStake = useMemo(() =>
    Math.max(1, ...((players || []).map(p => p.total_stake_eur || 0))),
  [players]);

  const groups = useMemo(() => {
    const byBucket = { high: [], mid: [], low: [], none: [] };
    for (const p of players || []) {
      byBucket[bucketOf(p)].push({
        user_id: p.user_id,
        email: p.email,
        x: Math.min(p.recent_vs_prior_stake_ratio || 0, X_CAP),
        y: Math.min(p.bonus_to_deposit_ratio || 0, Y_CAP),
        recent_vs_prior_stake_ratio: p.recent_vs_prior_stake_ratio,
        bonus_to_deposit_ratio: p.bonus_to_deposit_ratio,
        total_deposits_eur: p.total_deposits_eur,
        activation_rate: p.activation_rate,
        total_bonuses: p.total_bonuses,
        total_stake_eur: p.total_stake_eur,
      });
    }
    return byBucket;
  }, [players]);

  if (!players || players.length === 0) return null;

  const baseDomain = quadrantFilter ? QUADRANT_ZOOM[quadrantFilter] : { x: [0, X_CAP], y: [0, Y_CAP] };
  const xDomain = (dragZoom || baseDomain).x;
  const yDomain = (dragZoom || baseDomain).y;
  const isZoomed = !!dragZoom;

  const getRelativePos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const pos = getRelativePos(e);
    setSelecting(true);
    setSelStart(pos);
    setSelEnd(pos);
  };

  const handleMouseMove = (e) => {
    if (!selecting) return;
    setSelEnd(getRelativePos(e));
  };

  const handleMouseUp = () => {
    if (!selecting || !selStart || !selEnd) return;
    setSelecting(false);
    const rect = containerRef.current.getBoundingClientRect();
    const d1 = pixelToData(selStart.px, selStart.py, rect, xDomain, yDomain);
    const d2 = pixelToData(selEnd.px, selEnd.py, rect, xDomain, yDomain);
    const newX = [Math.min(d1.x, d2.x), Math.max(d1.x, d2.x)];
    const newY = [Math.min(d1.y, d2.y), Math.max(d1.y, d2.y)];
    // require a meaningful selection
    if (newX[1] - newX[0] > 0.05 && newY[1] - newY[0] > 0.02) {
      setDragZoom({ x: newX, y: newY });
    }
    setSelStart(null);
    setSelEnd(null);
  };

  // selection box in pixels
  const selBox = selecting && selStart && selEnd ? {
    left:   Math.min(selStart.px, selEnd.px),
    top:    Math.min(selStart.py, selEnd.py),
    width:  Math.abs(selEnd.px - selStart.px),
    height: Math.abs(selEnd.py - selStart.py),
  } : null;

  return (
    <div className="rounded border border-[#2a3042] bg-[#13161b] p-4">
      <div className="flex items-start justify-between mb-0.5">
        <p className="text-sm font-bold text-[#d8dde8]">Book triage map</p>
        {isZoomed && (
          <button
            onClick={() => setDragZoom(null)}
            className="text-[10px] px-2 py-0.5 rounded"
            style={{ background: '#1f2430', color: '#d4a843', border: '1px solid #3d4558', cursor: 'pointer' }}
          >
            ↩ Reset zoom
          </button>
        )}
      </div>
      <p className="mb-2 text-[11px] text-[#7a8499]">
        One dot per player · size = turnover · colour = activation rate · drag to zoom · click dot to open.
      </p>

      <div
        ref={containerRef}
        style={{ position: 'relative', userSelect: 'none', cursor: selecting ? 'crosshair' : 'default' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: PLOT.top, right: PLOT.right, bottom: 4, left: 0 }}>
            <CartesianGrid stroke="#1f2430" />
            <ReferenceArea x1={0} x2={X_SPLIT} y1={Y_SPLIT} y2={Y_CAP} fill="#e05555" fillOpacity={0.05} stroke="none"
              label={{ value: 'Retention risk', position: 'insideTopLeft', ...QUADRANT_LABEL_SX }} />
            <ReferenceArea x1={0} x2={X_SPLIT} y1={0} y2={Y_SPLIT} fill="#e0a23c" fillOpacity={0.04} stroke="none"
              label={{ value: 'Declining · low bonus', position: 'insideBottomLeft', ...QUADRANT_LABEL_SX }} />
            <ReferenceArea x1={X_SPLIT} x2={X_CAP} y1={Y_SPLIT} y2={Y_CAP} fill="#e0a23c" fillOpacity={0.04} stroke="none"
              label={{ value: 'Bonus-driven growth', position: 'insideTopRight', ...QUADRANT_LABEL_SX }} />
            <ReferenceArea x1={X_SPLIT} x2={X_CAP} y1={0} y2={Y_SPLIT} fill="#00c896" fillOpacity={0.05} stroke="none"
              label={{ value: 'Healthy', position: 'insideBottomRight', ...QUADRANT_LABEL_SX }} />
            <ReferenceLine x={X_SPLIT} stroke="#3d4558" strokeDasharray="3 3" />
            <ReferenceLine y={Y_SPLIT} stroke="#3d4558" strokeDasharray="3 3" />
            <XAxis
              type="number" dataKey="x" name="Recent÷prior turnover" domain={xDomain} allowDataOverflow
              padding={{ left: 16, right: 16 }}
              tick={{ fontSize: 11, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }}
              tickFormatter={(v) => v.toFixed(1) + '×'}
              label={{ value: 'Recent ÷ prior turnover', position: 'insideBottom', offset: -4, style: { fontSize: 10, fill: '#5a6477' } }}
            />
            <YAxis
              type="number" dataKey="y" name="Bonus÷deposits" domain={yDomain} allowDataOverflow width={64}
              padding={{ top: 16, bottom: 16 }}
              tick={{ fontSize: 11, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }}
              tickFormatter={(v) => Math.round(v * 100) + '%'}
              label={{ value: 'Bonus ÷ deposits', angle: -90, position: 'insideLeft', offset: 14, style: { fontSize: 10, fill: '#5a6477', textAnchor: 'middle' } }}
            />
            <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: '3 3', stroke: '#2a3042' }} />
            {BUCKETS.map((b) => (
              <Scatter
                key={b.key} name={b.label} data={groups[b.key]} fill={b.color}
                isAnimationActive={false}
                shape={(props) => <Dot {...props} onSelect={onSelect} maxStake={maxStake} />}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>

        {/* drag-selection overlay */}
        {selBox && (
          <div style={{
            position: 'absolute',
            left: selBox.left, top: selBox.top,
            width: selBox.width, height: selBox.height,
            background: 'rgba(212,168,67,0.08)',
            border: '1px solid rgba(212,168,67,0.5)',
            pointerEvents: 'none',
          }} />
        )}
      </div>

      <div className="mt-1 flex flex-wrap gap-1.5">
        {BUCKETS.map((b) => (
          <div key={b.key} className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
            <span className="text-[10px] text-[#7a8499]">{b.label}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {QUADRANTS.map((q) => (
          <div key={q.name} className="flex gap-2 rounded p-2 bg-[#0d0f12]" style={{ borderLeft: `3px solid ${q.color}` }}>
            <div className="min-w-0">
              <p className="text-[11.5px] font-bold text-[#d8dde8] m-0">
                {q.name}
                <span className="ml-1.5 text-[10px] text-[#5a6477]">{q.corner}</span>
              </p>
              <p className="mt-0.5 text-[11px] text-[#7a8499] m-0">{q.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-2 text-[10px] italic text-[#5a6477]">
        Caps: {X_CAP}× / {Y_CAP * 100}%. Drag to zoom into any region · ↩ Reset zoom to return.
      </p>
    </div>
  );
}
